import { Maximize, X, Link, Loader2 } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { loadHubIndex, hubHref, loadMoreByArtist } from "@/lib/hubs";
import { firstYear, fmtCount, hubPath, styleDisplayName, type HubIndex, type MoreByArtist } from "@shared/hubs";
import { museumValues } from "@shared/museums";
import { SiInstagram } from "react-icons/si";
import { FaXTwitter } from "react-icons/fa6";
import { useToast } from "@/hooks/use-toast";


class HTMLParser {
  static parse(html: string) {
    const elements = [];
    let key = 0;

    // Regular expression to match <a> and <em> tags
    const tagRegex = /<(a)\s+[^>]*?href=["']([^"']+)["'][^>]*?>(.*?)<\/\1>|<(em)>(.*?)<\/\4>/gi;
    
    let lastIndex = 0;
    let match;

    // Reset regex state
    tagRegex.lastIndex = 0;

    while ((match = tagRegex.exec(html)) !== null) {
      // Add text before the tag
      if (match.index > lastIndex) {
        const textBefore = html.slice(lastIndex, match.index);
        if (textBefore) {
          elements.push(textBefore);
        }
      }

      // Check if it's an <a> tag or <em> tag
      if (match[1] === 'a') {
        // It's a link
        const href = match[2];
        const linkText = match[3];
        elements.push(
          <a key={`link-${key++}`} href={href} className="text-blue-600 hover:underline">
            {linkText}
          </a>
        );
      } else if (match[4] === 'em') {
        // It's an em tag
        const emText = match[5];
        elements.push(
          <em key={`em-${key++}`}>{emText}</em>
        );
      }

      lastIndex = tagRegex.lastIndex;
    }

    // Add remaining text after the last tag
    if (lastIndex < html.length) {
      const textAfter = html.slice(lastIndex);
      if (textAfter) {
        elements.push(textAfter);
      }
    }

    return elements;
  }
}

interface ArtworkDisplayProps {
  artwork: any;
  showTitle: boolean;
}

export default function ArtworkDisplay({
  artwork,
  showTitle,
}: ArtworkDisplayProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const loadedIdRef = useRef<number | null>(null);
  const { toast } = useToast();
  const [hubIndex, setHubIndex] = useState<HubIndex | null>(null);
  useEffect(() => {
    let live = true;
    loadHubIndex().then((i) => live && setHubIndex(i));
    return () => {
      live = false;
    };
  }, []);
  const [moreBy, setMoreBy] = useState<MoreByArtist | null>(null);
  useEffect(() => {
    let live = true;
    setMoreBy(null);
    if (typeof artwork?.id === "number") loadMoreByArtist(artwork.id).then((m) => live && setMoreBy(m));
    return () => {
      live = false;
    };
  }, [artwork?.id]);
  const artistHref = hubHref(hubIndex, "artist", artwork.artist);
  const styleHref = hubHref(hubIndex, "style", artwork.style);
  const museums = museumValues(artwork.located_in);

  if (loadedIdRef.current !== artwork.id && isImageLoaded) {
    setIsImageLoaded(false);
  }

  var secondLine = artwork.made_of?.join(", ") ?? "";
  if (artwork.dimensions) {
    if (secondLine) secondLine += ", ";
    secondLine += artwork.dimensions;
  }

  const artworkWidth = showTitle ? "lg:col-span-2" : "lg:col-span-3";
  const artworkInformation = showTitle ? (
    <div className="space-y-8 slide-up">
      <div>
        {artistHref && (
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-2">
            <a href="/" className="hover:text-indigo-700 hover:underline">Home</a>
            {" › "}
            <a href="/artists/" className="hover:text-indigo-700 hover:underline">Artists</a>
            {" › "}
            <a href={artistHref} className="hover:text-indigo-700 hover:underline">{artwork.artist}</a>
          </nav>
        )}
        <h1 className="text-3xl font-semibold mb-2">{artwork.title}</h1>
        <p className="text-muted-foreground mb-1">
          {artistHref ? (
            <a href={artistHref} className="hover:text-indigo-700 hover:underline">{artwork.artist}</a>
          ) : (
            artwork.artist
          )}
          {artwork.date ? `, ${artwork.date}` : ""}
        </p>
        {artwork.style && (
          <p className="text-sm text-muted-foreground mb-1">
            {styleHref ? (
              <a href={styleHref} className="hover:text-indigo-700 hover:underline">{styleDisplayName(artwork.style)}</a>
            ) : (
              styleDisplayName(artwork.style)
            )}
          </p>
        )}
        {museums.length > 0 && (
          <p className="text-sm text-muted-foreground mb-1">
            {museums.map((m, i) => {
              const href = hubHref(hubIndex, "museum", m);
              return (
                <span key={m}>
                  {i > 0 ? ", " : ""}
                  {href ? <a href={href} className="hover:text-indigo-700 hover:underline">{m}</a> : m}
                </span>
              );
            })}
          </p>
        )}
        <p className="text-sm text-muted-foreground">{secondLine}</p>
      </div>
      <div className="prose prose-sm max-w-none">
        <div
          className="text-foreground/80 leading-relaxed"
        />
        {artwork.description ? HTMLParser.parse(artwork.description) : ""}
      </div>

      {moreBy && moreBy.works.length > 0 && (
        <section aria-label={`More by ${moreBy.artist}`}>
          <h2 className="text-lg font-semibold mb-2">More by {moreBy.artist}</h2>
          <ul className="text-sm space-y-1">
            {moreBy.works.map((w) => {
              const year = firstYear(w.date);
              return (
                <li key={w.id}>
                  <a href={`/artwork/${w.id}/`} className="hover:text-indigo-700 hover:underline">
                    {w.title}
                    {year ? ` (${year})` : ""}
                  </a>
                </li>
              );
            })}
          </ul>
          {moreBy.slug && (
            <p className="text-sm mt-2">
              <a href={hubPath("artist", moreBy.slug)} className="text-indigo-700 hover:underline">
                All {fmtCount(moreBy.count)} artworks by {moreBy.artist}
              </a>
            </p>
          )}
        </section>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => {
            const artworkUrl = `${window.location.origin}/artwork/${artwork.id}/`;
            const twitterText = `"${artwork.title}" by ${artwork.artist} - Discover this masterpiece at @ariadnesgallery`;
            const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(artworkUrl)}`;
            window.open(twitterUrl, '_blank', 'width=600,height=400,noopener,noreferrer');
          }}
          className="p-2 bg-black hover:bg-gray-800 text-white rounded-full transition-colors"
          data-testid="button-share-twitter"
          aria-label={`Share "${artwork.title}" on Twitter`}
          title="Share on Twitter"
        >
          <FaXTwitter className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            const artworkUrl = `${window.location.origin}/artwork/${artwork.id}/`;
            const instagramText = `"${artwork.title}" by ${artwork.artist} - Follow ariadnes.gallery for more art discoveries! ${artworkUrl}`;
            navigator.clipboard.writeText(instagramText).then(() => {
              toast({
                title: "Copied to clipboard!",
                description: "Instagram share text copied successfully.",
              });
            }).catch(() => {
              toast({
                title: "Copy failed",
                description: "Please manually copy the artwork URL to share.",
                variant: "destructive",
              });
            });
          }}
          className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-full transition-colors"
          data-testid="button-share-instagram"
          aria-label={`Copy Instagram share text for "${artwork.title}"`}
          title="Copy for Instagram"
        >
          <SiInstagram className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            const artworkUrl = `${window.location.origin}/artwork/${artwork.id}/`;
            navigator.clipboard.writeText(artworkUrl).then(() => {
              toast({
                title: "Link copied!",
                description: "Artwork link copied to clipboard.",
              });
            }).catch(() => {
              toast({
                title: "Copy failed",
                description: "Please manually copy the artwork URL.",
                variant: "destructive",
              });
            });
          }}
          className="p-2 bg-gray-600 hover:bg-gray-700 text-white rounded-full transition-colors"
          data-testid="button-share-link"
          aria-label={`Copy link for "${artwork.title}"`}
          title="Copy link"
        >
          <Link className="w-4 h-4" />
        </button>
      </div>

      {/* Additional Information */}
      {(artwork.movement || artwork.location || artwork.period) && (
        <div className="space-y-3">
          {artwork.movement && (
            <div className="flex items-center text-sm">
              <span className="font-medium mr-2 text-muted-foreground">
                Movement:
              </span>
              <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                {artwork.movement}
              </span>
            </div>
          )}
          {artwork.location && (
            <div className="flex items-center text-sm">
              <span className="font-medium mr-2 text-muted-foreground">
                Location:
              </span>
              <span>{artwork.location}</span>
            </div>
          )}
          {artwork.period && (
            <div className="flex items-center text-sm">
              <span className="font-medium mr-2 text-muted-foreground">
                Period:
              </span>
              <span>{artwork.period}</span>
            </div>
          )}
        </div>
      )}
    </div>
  ) : (
    ""
  );

  return (
    <>
      <section className="max-w-7xl mx-auto px-4 py-4 fade-in">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Primary Artwork Display */}
          <div className={artworkWidth}>
            <div
              className="relative group cursor-pointer"
              onClick={() => setIsFullscreen(true)}
            >
              <img
                key={artwork.id}
                src={artwork.imageUrl || (artwork as any).url}
                alt={`${artwork.title} by ${artwork.artist}`}
                className="w-full max-h-[75vh] object-contain transition-all duration-500 hover:opacity-90"
                onLoad={() => { loadedIdRef.current = artwork.id; setIsImageLoaded(true); }}
                onError={() => { loadedIdRef.current = artwork.id; setIsImageLoaded(true); }}
              />
              
              {/* Loading indicator */}
              {!isImageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                    <p className="text-sm text-gray-500">Loading artwork...</p>
                  </div>
                </div>
              )}
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="bg-black/50 rounded-full p-2">
                  <Maximize className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                "{artwork.title}" by {artwork.artist} – via Wikimedia Commons,
                CC BY-SA 3.0
              </div>
            </div>
          </div>

          {artworkInformation}
        </div>
      </section>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 z-10 bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={artwork.imageUrl || (artwork as any).url}
            alt={`${artwork.title} by ${artwork.artist}`}
            className="max-w-full max-h-full object-contain cursor-pointer"
            onClick={() => setIsFullscreen(false)}
          />
        </div>
      )}
    </>
  );
}
